/**
 * Interactive Fitzpatrick skin type quiz modal.
 *
 * Presents 8 questions about eye color, hair color, natural skin tone, freckling,
 * sun reaction, tanning ability, tan depth, and facial sensitivity. Scores are
 * summed and mapped to a Fitzpatrick type (I-VI). Auto-advances on answer
 * selection and lets the user apply the result to the main app's skin type slider.
 */
import React, { useState, useEffect } from 'react';
import { SKIN_TYPE_DATA } from './utils';
import './SkinTypeQuizModal.css';

const QUIZ_QUESTIONS = [
  {
    id: 1,
    question: "What is your natural eye color?",
    answers: [
      { text: "Light blue, light gray, or light green", points: 0 },
      { text: "Blue, gray, or green", points: 1 },
      { text: "Hazel", points: 2 },
      { text: "Light brown", points: 3 },
      { text: "Dark brown", points: 4 },
      { text: "Brownish black", points: 5 }
    ]
  },
  {
    id: 2,
    question: "What is your natural hair color?",
    answers: [
      { text: "Red or light blonde", points: 0 },
      { text: "Blonde", points: 1 },
      { text: "Dark blonde", points: 2 },
      { text: "Light brown", points: 3 },
      { text: "Dark brown", points: 4 },
      { text: "Black", points: 5 }
    ]
  },
  {
    id: 3,
    question: "What is your natural skin color (areas not exposed to sun)?",
    answers: [
      { text: "Pink", points: 0 },
      { text: "Pale", points: 7000 },
      { text: "Olive", points: 14000 },
      { text: "Light brown", points: 21000 },
      { text: "Brown", points: 28000 },
      { text: "Dark brown or black", points: 35000 }
    ]
  },
  {
    id: 4,
    question: "How many freckles do you have on unexposed areas of your skin?",
    answers: [
      { text: "Very many", points: 0 },
      { text: "Many", points: 1 },
      { text: "Several", points: 2 },
      { text: "A few", points: 3 },
      { text: "Almost none", points: 4 },
      { text: "None", points: 5 }
    ]
  },
  {
    id: 5,
    question: "How does your skin react to the sun (with no sunscreen)?",
    answers: [
      { text: "Always blisters and peels", points: 0 },
      { text: "Sometimes blisters and peels", points: 1 },
      { text: "Rarely blisters and peels", points: 2 },
      { text: "Burns sometimes", points: 3 },
      { text: "Burns rarely", points: 4 },
      { text: "Never burns", points: 5 }
    ]
  },
  {
    id: 6,
    question: "Does your skin tan (with no sunscreen)?",
    answers: [
      { text: "Never - I just burn and peel", points: 0 },
      { text: "Seldom", points: 1 },
      { text: "Sometimes", points: 2 },
      { text: "Often", points: 3 },
      { text: "Nearly always", points: 4 },
      { text: "Always", points: 5 }
    ]
  },
  {
    id: 7,
    question: "How deeply do you tan (with no sunscreen)?",
    answers: [
      { text: "Not at all", points: 0 },
      { text: "Minimal", points: 1 },
      { text: "Light tan", points: 2 },
      { text: "Moderate tan", points: 3 },
      { text: "Dark tan", points: 4 },
      { text: "Very dark tan", points: 5 }
    ]
  },
  {
    id: 8,
    question: "How sensitive is your face to the sun (with no sunscreen)?",
    answers: [
      { text: "Very sensitive", points: 0 },
      { text: "Sensitive", points: 1 },
      { text: "Somewhat sensitive", points: 2 },
      { text: "Somewhat resistant", points: 3 },
      { text: "Resistant", points: 4 },
      { text: "Very resistant - never had a problem", points: 5 }
    ]
  },
];

const calculateSkinType = (totalPoints) => {
  if (totalPoints <= 6000) return 1;
  if (totalPoints <= 13000) return 2;
  if (totalPoints <= 20000) return 3;
  if (totalPoints <= 27000) return 4;
  if (totalPoints <= 34000) return 5;
  return 6;
};

const SkinTypeQuizModal = ({ isOpen, onClose, onComplete, isDark }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);

  // Reset quiz when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentQuestion(0);
      setAnswers({});
      setShowResult(false);
      setResult(null);
    }
  }, [isOpen]);

  const handleAnswer = (questionId, points) => {
    const newAnswers = { ...answers, [questionId]: points };
    setAnswers(newAnswers);

    // Auto-advance to next question or submit if last question
    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQuestion(prev => prev + 1), 200);
    } else {
      // Last question - auto-submit
      setTimeout(() => {
        const totalPoints = Object.values(newAnswers).reduce((sum, pts) => sum + pts, 0);
        const skinType = calculateSkinType(totalPoints);
        setResult(skinType);
        setShowResult(true);
      }, 200);
    }
  };

  const handleNext = () => {
    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    const totalPoints = Object.values(answers).reduce((sum, pts) => sum + pts, 0);
    const skinType = calculateSkinType(totalPoints);
    setResult(skinType);
    setShowResult(true);
  };

  const handleConfirm = () => {
    onComplete(result);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const currentQ = QUIZ_QUESTIONS[currentQuestion];
  const isLastQuestion = currentQuestion === QUIZ_QUESTIONS.length - 1;
  const hasAnswered = answers[currentQ.id] !== undefined;
  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;

  if (showResult && result) {
    const skinData = SKIN_TYPE_DATA[result];
    return (
      <div className="quiz-modal-overlay" onClick={handleOverlayClick}>
        <div className={`quiz-modal-content ${isDark ? 'dark' : ''}`} onClick={(e) => e.stopPropagation()}>
          <button className="quiz-modal-close" onClick={onClose}>&times;</button>

          <div className="quiz-result">
            <h2>Your skin type result</h2>
            <div
              className="result-skin-swatch"
              style={{ backgroundColor: skinData.color }}
            />
            <div className="result-type">{skinData.label}</div>
            <div className="result-description">{skinData.description}</div>
            <button className="quiz-result-btn" onClick={handleConfirm}>
              Use this skin type
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-modal-overlay" onClick={handleOverlayClick}>
      <div className={`quiz-modal-content ${isDark ? 'dark' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button className="quiz-modal-close" onClick={onClose}>&times;</button>

        <div className="quiz-header">
          <h2>What's my skin type?</h2>
          <p>Answer these questions to find your <a href="https://dermnetnz.org/topics/skin-phototype" target="_blank" rel="noopener noreferrer" className="quiz-link">Fitzpatrick skin type</a></p>
        </div>

        <div className="quiz-progress">
          <div className="quiz-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="quiz-progress-text">
          Question {currentQuestion + 1} of {QUIZ_QUESTIONS.length}
        </div>

        <div className="quiz-question">{currentQ.question}</div>

        <div className="quiz-answers">
          {currentQ.answers.map((answer, index) => (
            <button
              key={index}
              className={`quiz-answer-option ${answers[currentQ.id] === answer.points ? 'selected' : ''}`}
              onClick={() => handleAnswer(currentQ.id, answer.points)}
            >
              {answer.text}
            </button>
          ))}
        </div>

        <div className="quiz-navigation">
          {currentQuestion > 0 ? (
            <button className="quiz-nav-btn back" onClick={handleBack}>
              Back
            </button>
          ) : (
            <div className="quiz-nav-spacer" />
          )}

          {isLastQuestion ? (
            <button
              className="quiz-nav-btn submit"
              onClick={handleSubmit}
              disabled={!hasAnswered}
            >
              See My Result
            </button>
          ) : (
            <button
              className="quiz-nav-btn next"
              onClick={handleNext}
              disabled={!hasAnswered}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkinTypeQuizModal;
